<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-body p-15 p-sm-20 p-md-25">
      <h2 class="fs-4 text-black fw-semibold mb-20">Détails et réponses de la session</h2>

      <div v-if="isLoading" class="text-center">
        <p class="mb-10">Chargement...</p>
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
      </div>

      <div v-else-if="session">
        <div class="row mb-20">
          <div class="col-md-6">
            <p><strong>Nombre de questions :</strong> {{ session.count }}</p>
            
            <p><strong>Date de création :</strong> {{ format_date(session.createdAt) }}</p>
          </div>
          <div class="col-md-6">
            <p v-if="selectedResult"><strong>Score :</strong> {{ selectedResult.score }} / {{ selectedResult.total }} ({{ selectedResult.percentage }}%)</p>
            <p v-if="selectedResult"><strong>Bonnes réponses :</strong> {{ selectedResult.correctAnswers }}</p>
            <p v-if="selectedResult"><strong>Mauvaises réponses :</strong> {{ selectedResult.incorrectAnswers }}</p>
            <p v-if="selectedResult"><strong>Date :</strong> {{ selectedResult.updatedAt ? format_date(selectedResult.updatedAt) : 'N/A' }}</p>
            <p v-else><strong>Statut :</strong> Aucun résultat disponible pour cette tentative</p>
          </div>
        </div>

        <div class="mb-20">
  <label for="attemptSelect" class="text-black fw-semibold mb-10">Sélectionner une tentative :</label>
  <select
    id="attemptSelect"
    v-model="selectedAttempt"
    class="form-select shadow-none text-black rounded-0"
    @change="loadResponsesForAttempt"
  >
    <option value="0">Session originale</option>
    <option v-for="result in filteredResultHistory" :key="result.attempt" :value="result.attempt">
      Reprise {{ result.attempt }}
    </option>
  </select>
</div>

        <h4 class="mb-10">Questions et réponses</h4>
        <div v-if="questionsWithAnswers.length > 0" class="table-responsive">
          <table class="table text-nowrap align-middle mb-0">
            <thead>
              <tr>
                <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                  Contenu
                </th>
                <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                  Réponse
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(question, index) in questionsWithAnswers" :key="index">
                <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                  {{ question.contenu }}
                </td>
                <td class="shadow-none lh-1 fw-medium">
                  <span :class="['text-black', { 'incorrect-strikethrough': !question.isCorrect }]">
                    {{ question.userAnswerText || 'Non répondu' }}
                  </span>
                  <div v-if="!question.isCorrect && question.correctAnswerText" class="mt-5 text-success">
                    <strong>Bonne réponse :</strong> <span class="fw-bold">{{ question.correctAnswerText }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="alert alert-info">
          Aucune réponse disponible pour cette tentative.
        </div>

        <div class="d-flex justify-content-center gap-2 mt-20">
          <router-link to="/liste-sessions" class="btn btn-primary">
            <i class="fa fa-arrow-left me-1"></i>
            Retour
          </router-link>
        </div>
      </div>

      <div v-else class="alert alert-danger text-center">
        <h4>Session non trouvée</h4>
        <p>Aucune session ne correspond à cet identifiant.</p>
        <router-link to="/liste-sessions" class="btn btn-primary">
          Retour à la liste
        </router-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { format_date, error } from '@/utils/utils';

interface Session {
  id: number;
  count: number;
  filiereNiveauMatiere: { id: number; titre: string } | null;
  createdAt: Date;
}

interface Question {
  id: number;
  contenu: string;
  type: string;
  propositions: Array<{ id: number; contenu: string; estLaBonneReponse: boolean }>;
}

interface Reponse {
  id: number;
  contenu: string | null;
  proposition: { id: number; contenu: string; estLaBonneReponse: boolean } | null;
  question: { id: number };
  attempt: number;
}

interface ResultHistory {
  attempt: number;
  score: number;
  total: number;
  percentage: number;
  correctAnswers: number;
  incorrectAnswers: number;
  updatedAt: Date | null;
}

interface QuestionWithAnswer {
  id: number;
  contenu: string;
  type: string;
  userAnswerText: string | null;
  correctAnswerText: string;
  isCorrect: boolean;
}

export default defineComponent({
  name: 'ViewSessionReponse',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const session = ref<Session | null>(null);
    const questions = ref<Question[]>([]);
    const reponses = ref<Reponse[]>([]);
    const resultHistory = ref<ResultHistory[]>([]);
    const selectedAttempt = ref<number>(0); // Par défaut, afficher la session originale
    const selectedResult = ref<ResultHistory | null>(null);
    const isLoading = ref(true);

    const filteredResultHistory = computed(() => {
      return resultHistory.value.filter(result => result.attempt !== 0);
    });

    const questionsWithAnswers = computed((): QuestionWithAnswer[] => {
      return questions.value.map((question) => {
        const userAnswer = reponses.value.find((rep) => rep.question.id === question.id && rep.attempt === selectedAttempt.value);
        let userAnswerText: string | null = 'Non répondu';
        let correctAnswerText = 'Non disponible';
        let isCorrect = false;

        const correctProposition = question.propositions?.find((p) => p.estLaBonneReponse);
        if (correctProposition) {
          correctAnswerText = correctProposition.contenu;
        }

        if (userAnswer) {
          if (question.type === 'choix_multiple' || question.type === 'oui_non') {
            userAnswerText = userAnswer.proposition ? userAnswer.proposition.contenu : userAnswer.contenu;
            isCorrect = userAnswer.proposition?.estLaBonneReponse || false;
          } else if (question.type === 'texte_libre') {
            userAnswerText = userAnswer.contenu;
            isCorrect = correctProposition
              ? userAnswer.contenu?.toLowerCase().trim() === correctProposition.contenu.toLowerCase().trim()
              : false;
          }
        }

        return {
          id: question.id,
          contenu: question.contenu,
          type: question.type,
          userAnswerText,
          correctAnswerText,
          isCorrect,
        };
      });
    });

    const loadSessionData = async () => {
      try {
        isLoading.value = true;
        const sessionId = route.params.id;

        // Charger les détails de la session
        const sessionResponse = await ApiService.get(`/sessions/${sessionId}`);
        if (sessionResponse.data.code === 200 && sessionResponse.data.data) {
          session.value = sessionResponse.data.data;
        } else {
          throw new Error('Session non trouvée');
        }

        // Charger les questions
        const questionsResponse = await ApiService.get(`/sessions/${sessionId}/questions`);
        if (questionsResponse.data.code === 200) {
          questions.value = questionsResponse.data.data;
        }

        // Charger l'historique des résultats
        const historyResponse = await ApiService.get(`/sessions/${sessionId}/history`);
        if (historyResponse.data.code === 200) {
          resultHistory.value = historyResponse.data.data.map((item: any) => ({
            attempt: item.attempt,
            score: item.score,
            total: item.total,
            percentage: item.percentage,
            correctAnswers: item.correctAnswers,
            incorrectAnswers: item.incorrectAnswers,
            updatedAt: item.updatedAt ? new Date(item.updatedAt) : null,
          }));
        }

        // Charger les réponses pour la tentative sélectionnée
        await loadResponsesForAttempt();
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des données de la session');
        session.value = null; // Assure que la session est null en cas d'erreur
      } finally {
        isLoading.value = false;
      }
    };

    const loadResponsesForAttempt = async () => {
      try {
        const sessionId = route.params.id;
        const attempt = selectedAttempt.value;
        const reponsesResponse = await ApiService.get(`/sessions/${sessionId}/reponses?attempt=${attempt}`);
        if (reponsesResponse.data.code === 200 && reponsesResponse.data.data) {
          reponses.value = reponsesResponse.data.data.reponses || [];
          selectedResult.value = resultHistory.value.find(r => r.attempt === attempt) || null;
        } else {
          reponses.value = [];
          selectedResult.value = null;
        }
      } catch (err: any) {
        console.error('Erreur lors du chargement des réponses pour la tentative:', err);
        error(err.response?.data?.message || 'Erreur lors du chargement des réponses');
        reponses.value = [];
        selectedResult.value = null;
      }
    };

    onMounted(() => {
      loadSessionData();
    });

    return {
      session,
      questions,
      reponses,
      resultHistory,
      filteredResultHistory,
      selectedAttempt,
      selectedResult,
      questionsWithAnswers,
      isLoading,
      format_date,
      loadResponsesForAttempt,
    };
  },
});
</script>

<style scoped>
.card {
  border-radius: 0;
}

.text-black {
  color: #212529;
}

.text-danger {
  color: #dc3545;
  font-size: 0.875rem;
}

.text-success {
  color: #198754;
}

.btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
  border-radius: 0;
  transition: all 0.2s ease;
}

.btn:hover {
  filter: brightness(90%);
}

.mb-10 {
  margin-bottom: 10px;
}

.mb-20 {
  margin-bottom: 20px;
}

.mt-5 {
  margin-top: 5px;
}

.mt-20 {
  margin-top: 20px;
}

.fs-4 {
  font-size: 1.5rem;
}

.alert {
  border-radius: 0;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}

.table-responsive {
  margin-bottom: 20px;
}

.form-select {
  border-radius: 0;
  border-color: #ced4da;
}

.incorrect-strikethrough {
  text-decoration: line-through;
  text-decoration-color: #dc3545;
  text-decoration-thickness: 2px;
}
</style>